import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface GeoSuggestion {
  name: string;
  postcode: string;
  lat: number;
  lng: number;
}

interface SearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (suggestion: GeoSuggestion) => void;
  placeholder?: string;
  className?: string;
}

const SearchAutocomplete = ({ value, onChange, onSelect, placeholder, className }: SearchAutocompleteProps) => {
  const [suggestions, setSuggestions] = useState<GeoSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      // Use French government geo API (free, no key needed)
      const isPostalCode = /^\d+$/.test(query);
      const endpoint = isPostalCode
        ? `https://geo.api.gouv.fr/communes?codePostal=${query}&fields=nom,codesPostaux,centre&limit=6`
        : `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(query)}&fields=nom,codesPostaux,centre&boost=population&limit=6`;

      const res = await fetch(endpoint);
      if (!res.ok) throw new Error();
      const data = await res.json();

      const mapped: GeoSuggestion[] = data.map((c: any) => ({
        name: c.nom,
        postcode: c.codesPostaux?.[0] ?? "",
        lat: c.centre?.coordinates?.[1] ?? 0,
        lng: c.centre?.coordinates?.[0] ?? 0,
      }));

      setSuggestions(mapped);
      setOpen(mapped.length > 0);
      setHighlightIndex(-1);
    } catch {
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value, fetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const select = (s: GeoSuggestion) => {
    onChange(s.name);
    onSelect?.(s);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      select(suggestions[highlightIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin z-10" />}
      <Input
        placeholder={placeholder ?? "Ville ou code postal..."}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onKeyDown={handleKeyDown}
        className={cn("pl-10 rounded-full h-12 text-base", className)}
        autoComplete="off"
      />
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl border bg-popover shadow-lg overflow-hidden">
          {suggestions.map((s, i) => (
            <button
              key={`${s.name}-${s.postcode}`}
              type="button"
              className={cn(
                "flex items-center gap-2 w-full px-4 py-2.5 text-sm text-left transition-colors",
                i === highlightIndex
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-accent/50"
              )}
              onMouseEnter={() => setHighlightIndex(i)}
              onClick={() => select(s)}
            >
              <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="font-medium">{s.name}</span>
              {s.postcode && (
                <span className="text-muted-foreground">({s.postcode})</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
